/** @jsx jsx */
import { Button, EmptyState, EmptyStateSuccess, Flex } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { motion } from "framer-motion";
import { FC, RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { jsx } from "theme-ui";

import { Wizard } from "@/components/wizards/Wizard";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { useRouter } from "@/router";
import { SimilarJobsTiles } from "@/views/job-requests/_components/SimilarJobs/SimilarJobsTiles";
import { router } from "@/views/router";

import { ApplyWizardContent } from "./_components/ApplyWizardContent";
import { useApplyWizard } from "./_components/ApplyWizardContext";

interface ApplyWizardProps {
  jobRequest: JobRequest;
  modalRef: RefObject<HTMLDivElement>;
  onViewThisApplicationClick: () => void;
}

export const ApplyWizard: FC<ApplyWizardProps> = ({ jobRequest, modalRef, onViewThisApplicationClick }) => {
  const { t } = useTranslation("session");

  const { draft } = useApplyWizard();

  const { pathname } = useLocation();

  const breakpoint = useBreakpointIndex();

  const { push } = useRouter();

  const isJobApplicationPath = pathname.startsWith(
    router.get("job-applications.details", { jobApplicationId: draft?.id }).path,
  );

  const handleViewThisApplicationButtonClick = useCallback(() => {
    if (isJobApplicationPath) {
      onViewThisApplicationClick();
    } else {
      push(router.get("job-applications.details", { jobApplicationId: draft!.id }).path);
    }
  }, [isJobApplicationPath, onViewThisApplicationClick, draft, push]);

  const handleViewJobsButtonClick = useCallback(() => {
    push(router.get("job-requests").path);
  }, [push]);

  return draft?.status === "SUBMITTED" ? (
    <Flex alignItems="center" justifyContent="center" px="sm" sx={{ py: 64, gap: 80 }} flexDirection="column">
      <EmptyState
        icon={<EmptyStateSuccess />}
        title={t("JOB_REQUESTS.APPLY.WIZARD.APPLICATION_SENT")}
        content={t("JOB_REQUESTS.APPLY.WIZARD.APPLICATION_SENT_TEXT", { jobRequestTitle: draft?.jobRequest?.title })}
        action={
          <Flex gap="xs">
            <Button variant="primary" onClick={handleViewJobsButtonClick}>
              {t("JOB_REQUESTS.APPLY.WIZARD.VIEW_JOBS")}
            </Button>
            <Button variant="secondary" onClick={handleViewThisApplicationButtonClick}>
              {t("JOB_REQUESTS.APPLY.WIZARD.VIEW_THIS_APPLICATION")}
            </Button>
          </Flex>
        }
      />
      {breakpoint > 2 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ transition: "easeIn", duration: 0.3, delay: 1 }}
          sx={{ width: "100%" }}>
          <SimilarJobsTiles jobRequestId={jobRequest.id} />
        </motion.div>
      )}
    </Flex>
  ) : (
    <Wizard>
      <ApplyWizardContent jobRequest={jobRequest} modalRef={modalRef} />
    </Wizard>
  );
};
